import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, TextField, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import { useAuthStatus } from '../Auth/AuthStatusContext';
import { useAuth } from 'react-oidc-context';

interface ProfileFormData {
    first_name: string;
    last_name: string;
    email: string;
}

const Profile: React.FC = () => {
    const { authStatus, groupStatus, profile } = useAuthStatus();
    const auth = useAuth();

    const [formData, setFormData] = useState<ProfileFormData>({
        first_name: '',
        last_name: '',
        email: ''
    });
    const [originalData,] = useState<ProfileFormData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Check if form has been modified
    const isDirty = originalData && (
        formData.first_name !== originalData.first_name ||
        formData.last_name !== originalData.last_name
    );

    // Reset form to original values
    const resetForm = useCallback(() => {
        if (originalData) {
            setFormData({ ...originalData });
        }
        setError('');
        setSuccess(false);
    }, [originalData]);

    // Initialize form with user data
    useEffect(() => {
        const loadProfile = async () => {
            if (!auth.user?.profile?.email) return;

            setIsLoading(true);
            setError('');

        };

        loadProfile();
    }, [authStatus, groupStatus, profile, auth.user?.profile?.email]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess(false);
        setIsSaving(true);

        try {
            if (!auth.user?.access_token) {
                throw new Error('You must be logged in to update your profile');
            }

        } catch (err) {
            console.error('Profile update error:', err);
            setError(err instanceof Error ? err.message : 'Failed to update profile');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
            <Paper sx={{ p: 3, width: '100%', maxWidth: 500 }}>
                <Typography variant="h5" gutterBottom>Profile Settings</Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
                        Profile updated successfully!
                    </Alert>
                )}
                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label="First Name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        margin="normal"
                        required
                        disabled={isSaving}
                    />
                    <TextField
                        fullWidth
                        label="Last Name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        margin="normal"
                        required
                        disabled={isSaving}
                    />
                    <TextField
                        fullWidth
                        label="Email"
                        value={formData.email}
                        margin="normal"
                        disabled
                        helperText="Email cannot be changed"
                    />
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={resetForm}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={!isDirty || isSaving}
                        >
                            {isSaving ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={20} />
                                    Saving...
                                </Box>
                            ) : 'Save Changes'}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
};

export default Profile;